import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft, ArrowRight, CheckCircle, FileText, Video, File, 
  ExternalLink, Download, HelpCircle, Image, Music, Clock,
  ChevronLeft, ChevronRight, Play, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const lessonTypeLabels = {
  text: 'Texte',
  video: 'Vidéo',
  pdf: 'PDF',
  link: 'Lien externe',
  file: 'Fichier',
  quiz: 'Quiz',
  image: 'Image',
  audio: 'Audio'
};

// Resolve a stored file URL to an absolute URL pointing at the backend.
const resolveFileUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  let path = url.startsWith('/uploads/content/') ? `/api${url}` : url;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

const StudentLessonPage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [startTime] = useState(Date.now());
  
  // Navigation helpers
  const [prevLesson, setPrevLesson] = useState(null);
  const [nextLesson, setNextLesson] = useState(null);
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch course structure
      const courseResponse = await api.get(`/content/courses/${courseId}`);
      setCourse(courseResponse.data);
      
      // Fetch lesson details
      const lessonResponse = await api.get(`/content/lessons/${lessonId}`);
      setLesson(lessonResponse.data);
      
      // Fetch progress
      try {
        const progressResponse = await api.get(`/content/student/course-progress/${courseId}`);
        setProgress(progressResponse.data);
      } catch (err) {
        setProgress({ completed_lesson_ids: [] });
      }
      
      // Find prev/next lessons
      const allLessons = [];
      courseResponse.data.modules?.forEach(module => {
        module.lessons?.filter(l => l.is_published).forEach(l => {
          allLessons.push({ ...l, moduleName: module.name });
        });
      });
      
      const currentIndex = allLessons.findIndex(l => l.id === parseInt(lessonId));
      if (currentIndex > 0) {
        setPrevLesson(allLessons[currentIndex - 1]);
      } else {
        setPrevLesson(null);
      }
      if (currentIndex < allLessons.length - 1) {
        setNextLesson(allLessons[currentIndex + 1]);
      } else {
        setNextLesson(null);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [courseId, lessonId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const isCompleted = progress?.completed_lesson_ids?.includes(parseInt(lessonId));
  
  const markAsComplete = async () => {
    if (isCompleted) return;
    
    setCompleting(true);
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 60000); // minutes
      await api.post(`/content/lessons/${lessonId}/complete?time_spent=${timeSpent}`);
      
      // Update local progress
      setProgress(prev => ({
        ...prev,
        completed_lesson_ids: [...(prev?.completed_lesson_ids || []), parseInt(lessonId)]
      }));
      
      toast.success('Leçon terminée !');
      
      // Navigate to next lesson if available
      if (nextLesson) {
        setTimeout(() => {
          navigate(`/student/courses/${courseId}/lessons/${nextLesson.id}`);
        }, 1000);
      }
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setCompleting(false);
    }
  };
  
  const goToQuiz = () => {
    if (lesson?.has_quiz && lesson?.quiz_id) {
      navigate(`/student/courses/${courseId}/quiz/${lesson.quiz_id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Leçon non trouvée</p>
          <Button onClick={() => navigate(`/student/courses/${courseId}`)} className="mt-4">
            Retour au cours
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(`/student/courses/${courseId}`)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-xs text-slate-500">{course?.name}</p>
              <h1 className="font-semibold text-slate-900 dark:text-white line-clamp-1">
                {lesson.name}
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge className="bg-violet-100 text-violet-700">
              {lessonTypeLabels[lesson.lesson_type]}
            </Badge>
            {isCompleted && (
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Terminé
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Lesson Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-6 lg:p-8">
                {/* TEXT LESSON */}
                {lesson.lesson_type === 'text' && (
                  <div className="prose dark:prose-invert max-w-none">
                    <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-300 leading-relaxed">
                      {lesson.content || 'Aucun contenu disponible.'}
                    </div>
                  </div>
                )}
                
                {/* VIDEO LESSON */}
                {lesson.lesson_type === 'video' && (
                  <div>
                    {lesson.video_url ? (
                      <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden mb-4">
                        {lesson.video_url.includes('youtube') || lesson.video_url.includes('youtu.be') ? (
                          <iframe
                            src={lesson.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                            className="w-full h-full"
                            allowFullScreen
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          />
                        ) : lesson.video_url.includes('vimeo') ? (
                          <iframe
                            src={lesson.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                            className="w-full h-full"
                            allowFullScreen
                          />
                        ) : (
                          <video
                            src={resolveFileUrl(lesson.video_url)}
                            controls
                            className="w-full h-full"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                        <Video className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    
                    {lesson.content && (
                      <div className="mt-4 prose dark:prose-invert max-w-none">
                        <h3>Description</h3>
                        <div className="whitespace-pre-wrap text-slate-600">
                          {lesson.content}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* PDF LESSON */}
                {lesson.lesson_type === 'pdf' && (
                  <div>
                    {lesson.document_url ? (
                      <>
                        <div className="flex items-center justify-between mb-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                              <File className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">Document PDF</p>
                              <p className="text-sm text-slate-500">{lesson.name}</p>
                            </div>
                          </div>
                          <Button onClick={() => window.open(resolveFileUrl(lesson.document_url), '_blank')}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Ouvrir
                          </Button>
                        </div>
                        <iframe
                          src={resolveFileUrl(lesson.document_url)}
                          title={lesson.name}
                          className="w-full h-[600px] rounded-lg border"
                        />
                      </>
                    ) : (
                      <div className="p-8 text-center text-slate-500">
                        <File className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Document non disponible</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* LINK LESSON */}
                {lesson.lesson_type === 'link' && (
                  <div className="space-y-4">
                    {lesson.external_url && (
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-6 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <ExternalLink className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-blue-900">Ressource externe</p>
                              <p className="text-sm text-blue-600 truncate max-w-md">
                                {lesson.external_url}
                              </p>
                            </div>
                          </div>
                          <Button onClick={() => window.open(lesson.external_url, '_blank')}>
                            Ouvrir le lien
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                    
                    {lesson.content && (
                      <div className="prose dark:prose-invert max-w-none">
                        <h3>Description</h3>
                        <div className="whitespace-pre-wrap text-slate-600">
                          {lesson.content}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* FILE/DOWNLOAD LESSON */}
                {lesson.lesson_type === 'file' && (
                  <div className="space-y-4">
                    {lesson.document_url && (
                      <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-6 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                              <Download className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-amber-900">Fichier à télécharger</p>
                              <p className="text-sm text-amber-600">{lesson.name}</p>
                            </div>
                          </div>
                          <Button onClick={() => window.open(resolveFileUrl(lesson.document_url), '_blank')}>
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                    
                    {lesson.content && (
                      <div className="prose dark:prose-invert max-w-none">
                        <h3>Instructions</h3>
                        <div className="whitespace-pre-wrap text-slate-600">
                          {lesson.content}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* QUIZ LESSON */}
                {lesson.lesson_type === 'quiz' && (
                  <div className="text-center py-8">
                    <HelpCircle className="w-16 h-16 text-violet-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                      Quiz disponible
                    </h2>
                    <p className="text-slate-600 mb-6">
                      Testez vos connaissances avec ce quiz
                    </p>
                    <Button 
                      size="lg" 
                      className="bg-violet-600 hover:bg-violet-700"
                      onClick={goToQuiz}
                    >
                      <Play className="w-5 h-5 mr-2" />
                      Commencer le quiz
                    </Button>
                  </div>
                )}
                
                {/* IMAGE LESSON */}
                {lesson.lesson_type === 'image' && (
                  <div>
                    {lesson.document_url ? (
                      <img 
                        src={resolveFileUrl(lesson.document_url)} 
                        alt={lesson.name}
                        className="max-w-full rounded-lg mx-auto"
                      />
                    ) : (
                      <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                        <Image className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    
                    {lesson.content && (
                      <div className="mt-4 prose dark:prose-invert max-w-none">
                        <p className="text-slate-600">{lesson.content}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* AUDIO LESSON */}
                {lesson.lesson_type === 'audio' && (
                  <div>
                    {lesson.document_url ? (
                      <div className="p-6 bg-slate-100 dark:bg-slate-800 rounded-lg">
                        <audio 
                          src={resolveFileUrl(lesson.document_url)} 
                          controls 
                          className="w-full"
                        />
                      </div>
                    ) : (
                      <div className="p-8 text-center text-slate-500">
                        <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Audio non disponible</p>
                      </div>
                    )}
                    
                    {lesson.content && (
                      <div className="mt-4 prose dark:prose-invert max-w-none">
                        <h3>Transcription</h3>
                        <div className="whitespace-pre-wrap text-slate-600">
                          {lesson.content}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation & Complete Button */}
            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => prevLesson && navigate(`/student/courses/${courseId}/lessons/${prevLesson.id}`)}
                disabled={!prevLesson}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
              
              {!isCompleted && lesson.lesson_type !== 'quiz' ? (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={markAsComplete}
                  disabled={completing}
                >
                  {completing ? (
                    'Validation...'
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Marquer comme terminé
                    </>
                  )}
                </Button>
              ) : isCompleted ? (
                <Badge className="bg-emerald-100 text-emerald-700 py-2 px-4">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Leçon terminée
                </Badge>
              ) : null}
              
              <Button
                variant="outline"
                onClick={() => nextLesson && navigate(`/student/courses/${courseId}/lessons/${nextLesson.id}`)}
                disabled={!nextLesson}
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {lesson.duration_minutes && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>{lesson.duration_minutes} minutes</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-600">
                  <BookOpen className="w-4 h-4" />
                  <span>{lessonTypeLabels[lesson.lesson_type]}</span>
                </div>
              </CardContent>
            </Card>
            
            {nextLesson && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Leçon suivante</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                    {nextLesson.name}
                  </p>
                  <Button 
                    size="sm" 
                    className="w-full"
                    onClick={() => navigate(`/student/courses/${courseId}/lessons/${nextLesson.id}`)}
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentLessonPage;
